import { useMemo } from 'react';

// mathjax-full (ONLY)
import { mathjax } from 'mathjax-full/js/mathjax';
import { TeX } from 'mathjax-full/js/input/tex';
import { CHTML } from 'mathjax-full/js/output/chtml';
import { liteAdaptor } from 'mathjax-full/js/adaptors/liteAdaptor';
import { RegisterHTMLHandler } from 'mathjax-full/js/handlers/html';
import style from '../../assets/styles/createQuestion.module.css';

/* ------------------ MathJax setup ------------------ */

const adaptor = liteAdaptor();
RegisterHTMLHandler(adaptor);

const tex = new TeX({
  inlineMath: [
    ['$', '$'],
    ['\\(', '\\)'],
  ],
  displayMath: [
    ['$$', '$$'],
    ['\\[', '\\]'],
  ],
});

const chtml = new CHTML({
  displayOverflow: 'linebreak',
  linebreaks: {
    automatic: true,
    width: 'container',
  },
  fontURL: 'https://cdn.jsdelivr.net/npm/mathjax@3/es5/output/chtml/fonts/woff-v2',
});

let cachedCSS = '';

function autoWrapLatexNodes(node: any) {
  if (!node) return;
  if (adaptor.kind(node) === '#text') {
    const text = adaptor.value(node);
    if (
      text &&
      /\\(left|right|frac|sqrt|sum|int|begin|alpha|beta|gamma|delta|theta|lambda|mu|pi|sigma|phi|omega|infty|pm|times|div|leq|geq|neq|approx|equiv|vec|hat|bar|tilde|mathcal|mathbf|mathrm|text|[a-zA-Z]+)/.test(
        text
      )
    ) {
      if (!/\$|\\\(|\\\[/.test(text)) {
        (node as any).value = `\\(${text}\\)`;
      }
    }
  } else if (node.children) {
    for (const child of node.children) {
      autoWrapLatexNodes(child);
    }
  }
}

function renderMathToHtml(htmlString: string): string {
  // ✅ Parse HTML STRING as a document
  const doc = adaptor.parse(htmlString, 'text/html');

  // ✅ Auto-wrap raw LaTeX text nodes that lack $ or \( delimiters
  autoWrapLatexNodes(doc.body);

  const mjDocument = mathjax.document(doc, {
    InputJax: tex,
    OutputJax: chtml,
  });

  // ✅ Render math inside real HTML nodes
  mjDocument.render();

  // ✅ Inject CSS once
  if (!cachedCSS) {
    cachedCSS = adaptor.outerHTML(chtml.styleSheet(mjDocument) as any);
  }

  // ✅ Return rendered body HTML (not text)
  return cachedCSS + adaptor.outerHTML(doc.body);
}

/* ------------------ React Component ------------------ */

type Props = {
  html: string;
  className?: string;
};

export default function MathJaxStaticRender({ html, className = '' }: Props) {
  const renderedHtml = useMemo(() => renderMathToHtml(html), [html]);

  return (
    <div className={style.math_preview_box}>
      <div className={style.math_preview_content} dangerouslySetInnerHTML={{ __html: renderedHtml }} />
    </div>
  );
}
